/**
 * Phase 7 centralized device management contracts, shared by @kortex/api and
 * (where the web admin needs the same shape) @kortex/web
 * (docs/android/PHASE-7-ARCHITECTURE.md).
 *
 * Kept in a separate file from sync.ts deliberately: sync.ts is the Phase 4
 * offline-sync wire protocol (push/pull of financial data, one very
 * different correctness contract), while this file is Phase 7's device
 * *management* surface (registration, configuration, commands, releases) -
 * mixing the two would make it harder to see which correctness rules apply
 * to which endpoint.
 */
import { z } from 'zod';
const uuid = z.string().uuid();
export const DEVICE_STATUSES = [
    'UNREGISTERED',
    'REGISTERED',
    'ACTIVE',
    'INACTIVE',
    'SUSPENDED',
    'DECOMMISSIONED',
];
export const deviceStatusEnum = z.enum(DEVICE_STATUSES);
export const DEVICE_ROLLOUT_GROUPS = ['PILOT', 'PRODUCTION'];
export const deviceRolloutGroupEnum = z.enum(DEVICE_ROLLOUT_GROUPS);
export const DEVICE_COMMAND_TYPES = [
    'REFRESH_CONFIGURATION',
    'SYNC_NOW',
    'DISABLE_DEVICE',
    'ENABLE_DEVICE',
    'RUN_DIAGNOSTIC',
    'WIPE_LOCAL_DATA',
];
export const deviceCommandTypeEnum = z.enum(DEVICE_COMMAND_TYPES);
export const DEVICE_COMMAND_STATUSES = [
    'PENDING',
    'RECEIVED',
    'PROCESSING',
    'COMPLETED',
    'FAILED',
    'EXPIRED',
];
export const deviceCommandStatusEnum = z.enum(DEVICE_COMMAND_STATUSES);
/** Simple, derived-at-read-time operational status - never stored (§21). */
export const DEVICE_HEALTH_STATUSES = ['HEALTHY', 'WARNING', 'CRITICAL', 'OFFLINE'];
export const deviceHealthStatusEnum = z.enum(DEVICE_HEALTH_STATUSES);
export const RELEASE_CHANNELS = ['STABLE', 'BETA'];
export const releaseChannelEnum = z.enum(RELEASE_CHANNELS);
export const RELEASE_STATUSES = ['DRAFT', 'PUBLISHED', 'DEPRECATED'];
export const releaseStatusEnum = z.enum(RELEASE_STATUSES);
export const UPDATE_READINESS = ['UP_TO_DATE', 'UPDATE_AVAILABLE', 'UPDATE_REQUIRED'];
export const updateReadinessEnum = z.enum(UPDATE_READINESS);
export const DEVICE_ERROR_SEVERITIES = ['INFO', 'WARNING', 'ERROR', 'CRITICAL'];
export const deviceErrorSeverityEnum = z.enum(DEVICE_ERROR_SEVERITIES);
/* ------------------------------------------------------------------ */
/* Pairing (admin generates, device redeems)                           */
/* ------------------------------------------------------------------ */
/** Admin side: no body beyond what auth/property context already carries. */
export const createPairingCodeSchema = z.object({
    /** Minutes until the code expires. Kept short - this is meant to be read
     *  off one screen and typed into a tablet within the same visit. */
    expiresInMinutes: z.number().int().min(1).max(60).default(15),
});
/** Device side: exchanges a still-valid code for its own credentials. */
export const redeemPairingCodeSchema = z.object({
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z2-9]{8}$/, 'That pairing code looks wrong'),
    deviceId: uuid,
    deviceCode: z
        .string()
        .trim()
        .regex(/^[A-Z][A-Z0-9]{1,3}$/, 'Use the device code shown on its diagnostics screen'),
    name: z.string().trim().min(1).max(60).optional(),
    androidVersion: z.string().trim().max(40).optional(),
    appVersion: z.string().trim().max(40).optional(),
});
/* ------------------------------------------------------------------ */
/* Device-facing: heartbeat, configuration, commands, error reports    */
/* ------------------------------------------------------------------ */
/** What a device reports of itself, roughly what Diagnostics.tsx already
 *  reads locally (§15/§17) - nothing here is new data, only newly sent. */
export const deviceHeartbeatSchema = z.object({
    appVersion: z.string().trim().max(40).optional(),
    androidVersion: z.string().trim().max(40).optional(),
    dbSchemaVersion: z.number().int().min(0).optional(),
    kioskActive: z.boolean().optional(),
    networkOnline: z.boolean().optional(),
    pendingSyncCount: z.number().int().min(0).optional(),
    failedSyncCount: z.number().int().min(0).optional(),
    lastSyncError: z.string().trim().max(500).nullable().optional(),
    batteryPercent: z.number().int().min(0).max(100).optional(),
    batteryCharging: z.boolean().optional(),
    storageTotalMb: z.number().int().min(0).optional(),
    storageAvailableMb: z.number().int().min(0).optional(),
});
export const deviceConfigAckSchema = z.object({
    version: z.number().int().min(0),
    applied: z.boolean(),
    /** Present when applied is false - why the device kept its previous
     *  configuration instead (§26-27). */
    error: z.string().trim().max(500).optional(),
});
export const deviceCommandAckSchema = z.object({
    status: z.enum(['RECEIVED', 'PROCESSING', 'COMPLETED', 'FAILED']),
    result: z.record(z.string(), z.unknown()).optional(),
    error: z.string().trim().max(500).optional(),
});
export const reportDeviceErrorSchema = z.object({
    code: z.string().trim().min(1).max(80),
    module: z.string().trim().min(1).max(80),
    severity: deviceErrorSeverityEnum.default('ERROR'),
    message: z.string().trim().min(1).max(2000),
    appVersion: z.string().trim().max(40).optional(),
    occurredAt: z.string().datetime().optional(),
});
/* ------------------------------------------------------------------ */
/* Admin-facing: assignment, configuration publish, commands, releases */
/* ------------------------------------------------------------------ */
export const deviceListQuerySchema = z.object({
    q: z.string().trim().max(100).optional(),
    status: deviceStatusEnum.optional(),
    health: deviceHealthStatusEnum.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export const assignDeviceSchema = z.object({
    name: z.string().trim().min(1).max(60).optional(),
    terminalNumber: z.string().trim().max(20).optional(),
    rolloutGroup: deviceRolloutGroupEnum.optional(),
});
export const disableDeviceSchema = z.object({
    reason: z.string().trim().min(1).max(300),
});
export const decommissionDeviceSchema = z.object({
    reason: z.string().trim().min(1).max(300),
});
/** A hotel's own device-configurable surface (§24) - deliberately not
 *  "any Android setting": the same handful of fields AppConfiguration.kt
 *  already exposes locally, now settable from the server too. */
export const deviceConfigPayloadSchema = z.object({
    terminalName: z.string().trim().min(1).max(60).optional(),
    printerTransport: z.enum(['', 'usb', 'bluetooth', 'lan']).optional(),
    printerAddress: z.string().trim().max(200).optional(),
    cashDrawerEnabled: z.boolean().optional(),
    customerDisplayMode: z.enum(['', 'mock']).optional(),
    receiptWidthMm: z.union([z.literal(58), z.literal(80)]).optional(),
    syncIntervalSeconds: z.number().int().min(5).max(600).optional(),
});
export const publishDeviceConfigSchema = z.object({
    config: deviceConfigPayloadSchema,
    note: z.string().trim().max(300).optional(),
});
export const issueDeviceCommandSchema = z.object({
    type: deviceCommandTypeEnum,
    payload: z.record(z.string(), z.unknown()).default({}),
    /** Minutes until the command expires unclaimed (§50). */
    expiresInMinutes: z.number().int().min(1).max(1440).default(60),
});
export const createReleaseSchema = z.object({
    version: z
        .string()
        .trim()
        .regex(/^\d+\.\d+\.\d+$/, 'Use semantic versioning, e.g. 1.4.0'),
    channel: releaseChannelEnum.default('STABLE'),
    minSupportedAndroidSdk: z.number().int().min(21).max(40).optional(),
    checksumSha256: z
        .string()
        .trim()
        .regex(/^[a-f0-9]{64}$/i, 'Expected a 64-character SHA-256 hex digest')
        .optional(),
    notes: z.string().trim().max(2000).optional(),
    rolloutGroups: z.array(deviceRolloutGroupEnum).min(1).default(['PRODUCTION']),
});
export const updateReleaseSchema = z.object({
    status: releaseStatusEnum.optional(),
    isMinimumSupported: z.boolean().optional(),
    isRecommended: z.boolean().optional(),
    rolloutGroups: z.array(deviceRolloutGroupEnum).min(1).optional(),
    notes: z.string().trim().max(2000).optional(),
});
export const DEVICE_UPDATE_ATTEMPT_STATUSES = [
    'DETECTED',
    'DOWNLOADING',
    'DOWNLOADED',
    'INSTALLING',
    'COMPLETED',
    'FAILED',
];
export const deviceUpdateAttemptStatusEnum = z.enum(DEVICE_UPDATE_ATTEMPT_STATUSES);
/**
 * A device reporting progress on an update it is (per its own local,
 * server-unobservable state machine) actually attempting - PHASE-7-
 * BASELINE-AUDIT.md §13: no real OTA installer exists yet, so this is
 * history/telemetry the future Android update flow will call into, not a
 * server-driven install trigger.
 */
export const reportUpdateProgressSchema = z.object({
    targetVersion: z
        .string()
        .trim()
        .regex(/^\d+\.\d+\.\d+$/, 'Use semantic versioning, e.g. 1.4.0'),
    status: deviceUpdateAttemptStatusEnum,
    errorMessage: z.string().trim().max(500).optional(),
});
//# sourceMappingURL=devices.js.map